/**
 * The signed-in identity. Rendered twice — as a row in the sidebar footer and
 * as an avatar in the top bar — from one menu definition, so the two can never
 * drift apart.
 */

import { Keyboard, LifeBuoy, LogOut, Moon, Sun, User as UserIcon, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { openShortcutSheet } from '@/components/app/ShortcutSheet';
import { themeCommandLabel } from '@/components/app/ThemeToggle';
import { Avatar, Menu, Skeleton, toast, type MenuItem } from '@/components/ui';
import { ROUTES } from '@/lib/constants';
import { useLogout } from '@/lib/queries';
import { useTheme } from '@/lib/theme';
import type { SessionResponse } from '@shared/types';

const ROLE_LABEL: Record<string, string> = {
  admin: 'Administrator',
  manager: 'Manager',
  agent: 'Agent',
  viewer: 'Viewer',
};

export interface UserMenuProps {
  session: SessionResponse | undefined;
  variant?: 'avatar' | 'row';
  side?: 'top' | 'bottom';
  align?: 'start' | 'center' | 'end';
}

export function UserMenu({ session, variant = 'avatar', side = 'bottom', align }: UserMenuProps) {
  const navigate = useNavigate();
  const logout = useLogout();
  const { theme, toggle } = useTheme();

  if (!session) {
    return variant === 'avatar' ? (
      <Skeleton w={26} h={26} rounded="full" />
    ) : (
      <div className="flex h-9 items-center gap-2 px-1">
        <Skeleton w={24} h={24} rounded="full" />
        <span className="flex flex-1 flex-col gap-1.5">
          <Skeleton w="60%" h={9} rounded="xs" />
          <Skeleton w="40%" h={7} rounded="xs" />
        </span>
      </div>
    );
  }

  const { user } = session;
  const canManageTeam = user.role === 'admin' || user.role === 'manager';

  const items: MenuItem[] = [
    {
      id: 'identity',
      type: 'label',
      label: (
        <span className="flex min-w-0 flex-col gap-0.5 py-0.5">
          <span className="truncate text-base font-medium text-primary">{user.name}</span>
          <span className="truncate text-xs font-normal text-muted">{user.email}</span>
        </span>
      ),
    },
    { id: 'sep-1', type: 'separator' },
    {
      id: 'profile',
      label: 'Profile & preferences',
      icon: UserIcon,
      onSelect: () => navigate(`${ROUTES.settings}/profile`),
    },
    ...(canManageTeam
      ? [
          {
            id: 'team',
            label: 'Team',
            icon: Users,
            onSelect: () => navigate(`${ROUTES.settings}/team`),
          } satisfies MenuItem,
        ]
      : []),
    /* Above the shortcut sheet on purpose. Somebody opening this menu to find
       help wants "what is this app" before "which key archives a record", and
       the two used to be one item that only ever answered the second question. */
    {
      id: 'getting-started',
      label: 'Getting started',
      icon: LifeBuoy,
      onSelect: () => navigate(ROUTES.gettingStarted),
    },
    {
      id: 'shortcuts',
      label: 'Keyboard shortcuts',
      icon: Keyboard,
      shortcut: ['?'],
      onSelect: () => openShortcutSheet(),
    },
    {
      id: 'theme',
      label: themeCommandLabel(theme),
      icon: theme === 'dark' ? Sun : Moon,
      shortcut: ['mod', 'shift', 'l'],
      onSelect: toggle,
    },
    { id: 'sep-2', type: 'separator' },
    {
      id: 'signout',
      label: 'Sign out',
      icon: LogOut,
      danger: true,
      disabled: logout.isPending,
      onSelect: () => {
        logout.mutate(undefined, {
          onSettled: () => {
            toast.success('Signed out');
            navigate(ROUTES.login, { replace: true });
          },
        });
      },
    },
  ];

  /**
   * Two tab stops, two names.
   *
   * Both triggers open the same menu, and both used to announce "Account —
   * Nadia Rahimi, button" — positions 10 and 17 of 26 on a list screen, byte
   * identical, with nothing to tell a screen-reader user which one they had
   * landed on or that the second was the same control again. They are named for
   * what they are where they are instead: the sidebar footer *states* who is
   * signed in (it draws the name and the role), the top bar's avatar is the
   * menu's button.
   */
  const roleLabel = ROLE_LABEL[user.role] ?? user.role;

  const trigger =
    variant === 'avatar' ? (
      <button
        type="button"
        aria-label={`Account menu — ${user.name}`}
        className={[
          'flex size-[var(--h-control)] shrink-0 items-center justify-center rounded-[var(--radius-full)]',
          'transition-colors ease-[var(--ease-out)] duration-[var(--dur-fast)]',
          'hover:bg-[var(--surface-hover)] active:bg-[var(--surface-active)]',
          'focus-visible:outline-2 focus-visible:outline-offset-1 outline-[color:var(--focus)]',
          'data-[state=open]:bg-[var(--surface-active)]',
        ].join(' ')}
      >
        <Avatar name={user.name} seed={user.avatarSeed} size="sm" />
      </button>
    ) : (
      <button
        type="button"
        aria-label={`Signed in as ${user.name}, ${roleLabel}`}
        className={[
          'flex h-9 w-full items-center gap-2 rounded-[var(--radius-sm)] px-1.5 text-left',
          'transition-colors ease-[var(--ease-out)] duration-[var(--dur-fast)]',
          'hover:bg-[var(--surface-hover)] active:bg-[var(--surface-active)]',
          'focus-visible:outline-2 focus-visible:outline-offset-[-2px] outline-[color:var(--focus)]',
          'data-[state=open]:bg-[var(--surface-active)]',
        ].join(' ')}
      >
        <Avatar name={user.name} seed={user.avatarSeed} size="sm" />
        <span className="flex min-w-0 flex-1 flex-col leading-none">
          <span className="truncate text-base font-medium text-primary">{user.name}</span>
          <span className="mt-1 truncate text-xs text-faint">{roleLabel}</span>
        </span>
      </button>
    );

  return (
    <Menu
      items={items}
      trigger={trigger}
      side={side}
      align={align ?? (variant === 'avatar' ? 'end' : 'start')}
      width={variant === 'avatar' ? 232 : 216}
      label="Account"
    />
  );
}
